import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../../services';
import { utils } from '../../../utils';
import FormLayout from '../../../components/Common/FormLayout';

const TireCreate: React.FC = () => {
  const navigate = useNavigate();
  const [veiculos, setVeiculos] = useState<any[]>([]);
  const [formData, setFormData] = useState({
    codigo: '', marca: '', modelo: '', medida: '', dot: '', numero_fogo: '',
    valor_unitario: '' as number | '', data_compra: '', fornecedor: '', nota_fiscal: '',
    veiculo_id: '' as number | '', posicao: '',
    sulco_inicial: '' as number | '', sulco_minimo: 1.6,
    status: 'estoque', vida_util_estimada_km: '' as number | '', observacoes: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string | undefined>>({});

  useEffect(() => { loadVehicles(); }, []);

  const loadVehicles = async () => {
    try {
      const response = await apiService.getVehicles({ page: 1, limit: 100 });
      if (response.success && response.data) setVeiculos(response.data || []);
    } catch (error) { console.error('Erro ao carregar veiculos:', error); }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: undefined }));
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.marca.trim()) newErrors.marca = 'Marca e obrigatoria';
    if (!formData.medida.trim()) newErrors.medida = 'Medida e obrigatoria';
    if (!formData.sulco_inicial || Number(formData.sulco_inicial) <= 0) newErrors.sulco_inicial = 'Sulco inicial deve ser maior que zero';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) { utils.showNotification('Corrija os erros no formulario', { type: 'error' }); return; }

    setIsLoading(true);
    try {
      const submitData = {
        codigo: formData.codigo || undefined,
        marca: formData.marca,
        modelo: formData.modelo || undefined,
        medida: formData.medida,
        dot: formData.dot || undefined,
        numero_fogo: formData.numero_fogo || undefined,
        valor_unitario: formData.valor_unitario === '' ? undefined : Number(formData.valor_unitario),
        data_compra: formData.data_compra || undefined,
        fornecedor: formData.fornecedor || undefined,
        nota_fiscal: formData.nota_fiscal || undefined,
        veiculo_id: formData.veiculo_id === '' ? undefined : Number(formData.veiculo_id),
        posicao: formData.posicao || undefined,
        sulco_inicial: Number(formData.sulco_inicial),
        sulco_minimo: Number(formData.sulco_minimo),
        status: formData.status,
        vida_util_estimada_km: formData.vida_util_estimada_km === '' ? undefined : Number(formData.vida_util_estimada_km),
        observacoes: formData.observacoes || undefined
      };

      const response = await apiService.createPneu(submitData);
      if (response.success) {
        utils.showNotification('Pneu cadastrado com sucesso!', { type: 'success' });
        navigate('/pneus');
      } else {
        throw new Error(response.message || 'Erro ao cadastrar pneu');
      }
    } catch (error: any) {
      utils.showNotification(error.message || 'Erro ao cadastrar pneu', { type: 'error' });
    } finally { setIsLoading(false); }
  };

  return (
    <FormLayout title="Novo Pneu" subtitle="Cadastro de Pneu" onCancel={() => navigate('/pneus')}
      onSave={() => { const e = { preventDefault: () => {} } as React.FormEvent; handleSubmit(e); }}
      saveButtonText="Cadastrar Pneu" isLoading={isLoading} showActions={false}>
      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: '30px' }}>
          <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
            Identificacao do Pneu
          </h3>
          <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))' }}>
            <div className="form-group">
              <label className="form-label">Marca *</label>
              <input type="text" name="marca" value={formData.marca} onChange={handleInputChange}
                className={`form-control ${errors.marca ? 'error' : ''}`} placeholder="Ex: Bridgestone, Michelin" />
              {errors.marca && <span className="error-message">{errors.marca}</span>}
            </div>
            <div className="form-group">
              <label className="form-label">Modelo</label>
              <input type="text" name="modelo" value={formData.modelo} onChange={handleInputChange}
                className="form-control" placeholder="Ex: Duravis R660" />
            </div>
            <div className="form-group">
              <label className="form-label">Medida *</label>
              <input type="text" name="medida" value={formData.medida} onChange={handleInputChange}
                className={`form-control ${errors.medida ? 'error' : ''}`} placeholder="Ex: 295/80R22.5" />
              {errors.medida && <span className="error-message">{errors.medida}</span>}
            </div>
            <div className="form-group">
              <label className="form-label">DOT</label>
              <input type="text" name="dot" value={formData.dot} onChange={handleInputChange}
                className="form-control" placeholder="Ex: 2425" maxLength={10} />
            </div>
            <div className="form-group">
              <label className="form-label">Numero de Fogo</label>
              <input type="text" name="numero_fogo" value={formData.numero_fogo} onChange={handleInputChange}
                className="form-control" placeholder="Numero gravado no pneu" />
            </div>
            <div className="form-group">
              <label className="form-label">Codigo Interno</label>
              <input type="text" name="codigo" value={formData.codigo} onChange={handleInputChange}
                className="form-control" placeholder="Codigo interno" />
            </div>
          </div>
        </div>

        <div style={{ marginBottom: '30px' }}>
          <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
            Sulco e Posicao
          </h3>
          <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))' }}>
            <div className="form-group">
              <label className="form-label">Sulco Inicial (mm) *</label>
              <input type="number" name="sulco_inicial" value={formData.sulco_inicial} onChange={handleInputChange}
                className={`form-control ${errors.sulco_inicial ? 'error' : ''}`} step="0.1" min="0" placeholder="Ex: 16.0" />
              {errors.sulco_inicial && <span className="error-message">{errors.sulco_inicial}</span>}
            </div>
            <div className="form-group">
              <label className="form-label">Sulco Minimo (mm)</label>
              <input type="number" name="sulco_minimo" value={formData.sulco_minimo} onChange={handleInputChange}
                className="form-control" step="0.1" min="0" />
            </div>
            <div className="form-group">
              <label className="form-label">Veiculo</label>
              <select name="veiculo_id" value={formData.veiculo_id} onChange={handleInputChange} className="form-control">
                <option value="">Nenhum (estoque)</option>
                {veiculos.map(v => <option key={v.id} value={v.id}>{v.placa} - {v.marca} {v.modelo}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Posicao</label>
              <select name="posicao" value={formData.posicao} onChange={handleInputChange} className="form-control">
                <option value="">Selecione</option>
                <option value="DE">Dianteiro Esquerdo</option>
                <option value="DD">Dianteiro Direito</option>
                <option value="TIE">Traseiro Interno Esquerdo</option>
                <option value="TEE">Traseiro Externo Esquerdo</option>
                <option value="TID">Traseiro Interno Direito</option>
                <option value="TED">Traseiro Externo Direito</option>
                <option value="EI">Eixo Intermediario Esquerdo</option>
                <option value="EID">Eixo Intermediario Direito</option>
                <option value="ESTEPE">Estepe</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Status</label>
              <select name="status" value={formData.status} onChange={handleInputChange} className="form-control">
                <option value="novo">Novo</option>
                <option value="em_uso">Em Uso</option>
                <option value="estoque">Estoque</option>
                <option value="recapagem">Recapagem</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Vida Util Estimada (km)</label>
              <input type="number" name="vida_util_estimada_km" value={formData.vida_util_estimada_km} onChange={handleInputChange}
                className="form-control" min="0" placeholder="Ex: 80000" />
            </div>
          </div>
        </div>

        <div style={{ marginBottom: '30px' }}>
          <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
            Dados de Compra
          </h3>
          <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))' }}>
            <div className="form-group">
              <label className="form-label">Valor Unitario</label>
              <input type="number" name="valor_unitario" value={formData.valor_unitario} onChange={handleInputChange}
                className="form-control" step="0.01" min="0" placeholder="R$" />
            </div>
            <div className="form-group">
              <label className="form-label">Data da Compra</label>
              <input type="date" name="data_compra" value={formData.data_compra} onChange={handleInputChange} className="form-control" />
            </div>
            <div className="form-group">
              <label className="form-label">Fornecedor</label>
              <input type="text" name="fornecedor" value={formData.fornecedor} onChange={handleInputChange}
                className="form-control" placeholder="Nome do fornecedor" />
            </div>
            <div className="form-group">
              <label className="form-label">Nota Fiscal</label>
              <input type="text" name="nota_fiscal" value={formData.nota_fiscal} onChange={handleInputChange}
                className="form-control" placeholder="Numero da NF" />
            </div>
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Observacoes</label>
          <textarea name="observacoes" value={formData.observacoes} onChange={handleInputChange}
            className="form-control" rows={3} placeholder="Observacoes adicionais..." />
        </div>

        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
          <button type="button" className="btn btn-secondary" onClick={() => navigate('/pneus')}>Cancelar</button>
          <button type="submit" className="btn btn-primary" disabled={isLoading}>
            {isLoading ? 'Cadastrando...' : 'Cadastrar Pneu'}
          </button>
        </div>
      </form>
    </FormLayout>
  );
};

export default TireCreate;
